import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, input, OnDestroy, signal, untracked } from '@angular/core';

import {
  InfoscreenFeed,
  InfoscreenFeedAd,
  InfoscreenNewsItem,
  InfoscreenTextAlign,
  InfoscreenTextSize,
  InfoscreenWeather,
} from '../infoscreen-module.types';
import { adPageSeconds } from '../scroll-speed';
import { WeatherCardComponent } from '../weather/weather-card.component';
import { textBlocks } from './welcome-text';

/** The feed's size and alignment for the welcome text and the body text. */
export type WelcomeStyling = Partial<
  Pick<InfoscreenFeed, 'welcomeTextSize' | 'welcomeTextAlign' | 'welcomeBodySize' | 'welcomeBodyAlign'>
>;

const SIZE_CLASSES: Record<InfoscreenTextSize, string> = { Small: 'size-small', Normal: 'size-normal', Large: 'size-large' };
const ALIGN_CLASSES: Record<InfoscreenTextAlign, string> = { Left: 'align-left', Center: 'align-center', Right: 'align-right' };

/** `size-large align-left` …; normal and centred when not sent, or sent as a value this app does not know. */
function styleClass(size?: InfoscreenTextSize, align?: InfoscreenTextAlign): string {
  return `${SIZE_CLASSES[size!] ?? 'size-normal'} ${ALIGN_CLASSES[align!] ?? 'align-center'}`;
}

/** Headlines on the news card. */
const NEWS_ON_CARD = 4;

/** Length of the fade between two ads. */
const FADE_MS = 600;

/**
 * A welcome text and the location's ads, large and one at a time — the whole of the welcome
 * view, or the pane beside the times on a split screen (`compact`: smaller type, for a narrower
 * space). The ads change at the screen's ad speed (`adPageSeconds`) and fade between each other;
 * without ads the text has the space to itself, and without a text the ads do.
 */
@Component({
  selector: 'app-welcome-pane',
  imports: [NgTemplateOutlet, WeatherCardComponent],
  template: `
    @if (text() || body()) {
      <div class="intro">
        <!-- The headline as written; the body text with its lists, bold and italic. -->
        @if (text()) {
          <div class="welcome" [class]="textClass()">{{ text() }}</div>
        }
        @if (body()) {
          <div class="welcome-body" [class]="bodyClass()">
            @for (block of bodyBlocks(); track $index) {
              @switch (block.kind) {
                @case ('bullets') {
                  <ul class="list">
                    @for (item of block.items; track $index) {
                      <li><ng-container *ngTemplateOutlet="runs; context: { $implicit: item }" /></li>
                    }
                  </ul>
                }
                @case ('numbers') {
                  <ol class="list">
                    @for (item of block.items; track $index) {
                      <li><ng-container *ngTemplateOutlet="runs; context: { $implicit: item }" /></li>
                    }
                  </ol>
                }
                @case ('divider') {
                  <hr class="divider" />
                }
                @case ('lines') {
                  <div class="lines"><ng-container *ngTemplateOutlet="runs; context: { $implicit: block.parts }" /></div>
                }
              }
            }
          </div>
        }
      </div>
    }
    <!-- On one line: with pre-line, a line break between the runs would show. -->
    <ng-template #runs let-parts>@for (part of parts; track $index) {<span [class.bold]="part.bold" [class.italic]="part.italic">{{ part.text }}</span>}</ng-template>
    @if (current(); as ad) {
      <div class="showcase" [class.fading]="fading()">
        @if (ad.type === 'Weather') {
          @if (weather(); as weather) {
            <app-weather-card class="ad weather" [weather]="weather" />
          }
        } @else if (ad.type === 'News') {
          <div class="ad news">
            <div class="news-source">Nyheder fra {{ newsSource() }}</div>
            @for (item of newsOnCard(); track $index) {
              <div class="news-item">
                @if (mixedNews() && item.source) {
                  <span class="news-from">{{ item.source }}:</span>
                }
                {{ item.title }}
              </div>
            }
          </div>
        } @else if (ad.type === 'Image' && ad.imageUrl) {
          <img class="ad image" [src]="ad.imageUrl" alt="" />
        } @else {
          <div class="ad text" [style.background]="ad.backgroundColor || '#ffffff'" [style.color]="ad.textColor || '#1d2a44'">
            @if (ad.headline) {
              <div class="headline">{{ ad.headline }}</div>
            }
            @if (ad.body) {
              <div class="body">{{ ad.body }}</div>
            }
          </div>
        }
      </div>
      @if (ads().length > 1) {
        <div class="dots" aria-hidden="true">
          @for (ad of ads(); track ad.id; let i = $index) {
            <span [class.on]="i === index()"></span>
          }
        </div>
      }
    }
  `,
  styles: `
    :host {
      --u: calc(var(--screen-h, 100vh) / 100); /* 1 % of the screen's height */
      display: flex;
      flex-direction: column;
      gap: calc(var(--u) * 3);
      height: 100%;
      box-sizing: border-box;
      padding: calc(var(--u) * 4) var(--gutter-size, 4%);
      color: rgb(var(--ink-rgb, 255, 255, 255));
    }
    :host(.compact) {
      padding: calc(var(--u) * 2.5) calc(var(--u) * 2.5);
      gap: calc(var(--u) * 2);
      background: rgba(var(--ink-rgb, 255, 255, 255), 0.06);
    }
    .intro {
      display: flex;
      flex-direction: column;
      gap: calc(var(--u) * 1.6);
      flex-shrink: 0;
      white-space: pre-line;
    }
    /* Each text's own size and alignment: --scale times the view's size, and the side it keeps to. */
    .size-small {
      --scale: 0.75;
    }
    .size-large {
      --scale: 1.35;
    }
    .align-left {
      align-self: flex-start;
      text-align: left;
    }
    .align-center {
      align-self: center;
      text-align: center;
    }
    .align-right {
      align-self: flex-end;
      text-align: right;
    }
    .welcome {
      font-weight: 700;
      line-height: 1.15;
      font-size: calc(var(--u) * 7 * var(--scale, 1));
    }
    :host(.compact) .welcome {
      font-size: calc(var(--u) * 4.2 * var(--scale, 1));
    }
    /* The body text: smaller, in lines a comfortable length. */
    .welcome-body {
      max-width: 60ch;
      font-weight: 500;
      line-height: 1.35;
      font-size: calc(var(--u) * 3.2 * var(--scale, 1));
      opacity: 0.92;
    }
    /* A list keeps its bullets or numbers at its left edge, and lines up as a whole with the text's
       alignment: a block of its own as wide as its items, placed to the side the text keeps to. */
    .list {
      display: block;
      width: fit-content;
      margin: 0.15em 0;
      padding-left: 1.3em;
      text-align: left;
      white-space: normal;
    }
    .align-center .list {
      margin-inline: auto;
    }
    .align-right .list {
      margin-left: auto;
    }
    .list li + li {
      margin-top: 0.15em;
    }
    /* A divider in the body text: a thin line across it, in the text's colour. */
    .divider {
      width: 100%;
      min-width: 6em;
      margin: 0.5em 0;
      border: 0;
      border-top: max(1px, 0.08em) solid currentColor;
      opacity: 0.6;
    }
    /* The body text's bold and italic runs: bold as bold as the welcome text. */
    .bold {
      font-weight: 800;
    }
    .italic {
      font-style: italic;
    }
    :host(.compact) .welcome-body {
      font-size: calc(var(--u) * 2.2 * var(--scale, 1));
    }
    /* With nothing to show beside it, the text takes the middle of the screen. */
    .intro:only-child {
      margin: auto 0;
      .welcome {
        font-size: calc(var(--u) * 9 * var(--scale, 1));
      }
      .welcome-body {
        font-size: calc(var(--u) * 4 * var(--scale, 1));
      }
    }
    :host(.compact) .intro:only-child {
      .welcome {
        font-size: calc(var(--u) * 5.5 * var(--scale, 1));
      }
      .welcome-body {
        font-size: calc(var(--u) * 2.8 * var(--scale, 1));
      }
    }
    .showcase {
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity ${FADE_MS}ms ease-in-out;
      &.fading {
        opacity: 0;
      }
    }
    /* An ad has its own background: no halo from the theme's decorations on its text. */
    .ad {
      text-shadow: none;
    }
    .ad.image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: calc(var(--u) * 1.2);
      box-shadow: 0 calc(var(--u) * 0.6) calc(var(--u) * 2.5) rgba(0, 0, 0, 0.3);
    }
    .ad.text {
      box-sizing: border-box;
      width: 100%;
      max-height: 100%;
      overflow: hidden;
      border-radius: calc(var(--u) * 1.2);
      padding: calc(var(--u) * 4);
      text-align: center;
      box-shadow: 0 calc(var(--u) * 0.6) calc(var(--u) * 2.5) rgba(0, 0, 0, 0.3);
    }
    .ad.weather {
      font-size: calc(var(--u) * 4.2);
    }
    :host(.compact) .ad.weather {
      font-size: calc(var(--u) * 2.4);
      white-space: normal;
      flex-wrap: wrap;
      justify-content: center;
    }
    .ad.news {
      box-sizing: border-box;
      width: 100%;
      max-height: 100%;
      overflow: hidden;
      border-radius: calc(var(--u) * 1.2);
      padding: calc(var(--u) * 3);
      background: rgba(0, 0, 0, 0.3);
      color: #ffffff;
      display: flex;
      flex-direction: column;
      gap: calc(var(--u) * 1.6);
    }
    .news-source {
      font-weight: 800;
      font-size: calc(var(--u) * 3);
      color: var(--theme-accent, #32bad4);
    }
    .news-item {
      font-size: calc(var(--u) * 3.2);
      line-height: 1.25;
      padding-left: 0.8em;
      border-left: 0.2em solid rgba(255, 255, 255, 0.4);
    }
    .news-from {
      font-weight: 700;
      opacity: 0.85;
      margin-right: 0.3em;
    }
    :host(.compact) .news-source {
      font-size: calc(var(--u) * 2.2);
    }
    :host(.compact) .news-item {
      font-size: calc(var(--u) * 2.2);
    }
    .headline {
      font-size: calc(var(--u) * 6);
      font-weight: 700;
      line-height: 1.1;
    }
    .body {
      margin-top: calc(var(--u) * 1.5);
      font-size: calc(var(--u) * 3.4);
      line-height: 1.3;
      white-space: pre-line;
    }
    :host(.compact) .headline {
      font-size: calc(var(--u) * 3.8);
    }
    :host(.compact) .body {
      font-size: calc(var(--u) * 2.4);
    }
    .dots {
      display: flex;
      justify-content: center;
      gap: calc(var(--u) * 0.8);
      span {
        width: calc(var(--u) * 1);
        height: calc(var(--u) * 1);
        border-radius: 50%;
        background: rgba(var(--ink-rgb, 255, 255, 255), 0.3);
        &.on {
          background: rgb(var(--ink-rgb, 255, 255, 255));
        }
      }
    }
  `,
  host: { '[class.compact]': 'compact()' },
})
export class WelcomePaneComponent implements OnDestroy {
  readonly text = input<string | null>(null);
  /** The body text under the welcome text, smaller. */
  readonly body = input<string | null>(null);
  /** The two texts' size and alignment, as the feed sends them. */
  readonly styling = input<WelcomeStyling>({});
  readonly bodyBlocks = computed(() => textBlocks(this.body()));
  /** `size-large align-left` … — normal and centred unless chosen. */
  readonly textClass = computed(() => styleClass(this.styling().welcomeTextSize, this.styling().welcomeTextAlign));
  readonly bodyClass = computed(() => styleClass(this.styling().welcomeBodySize, this.styling().welcomeBodyAlign));
  readonly ads = input<InfoscreenFeedAd[]>([]);
  /** The screen's ad speed: how long each ad stays (5 → 10 s). */
  readonly speed = input(5);
  /** The pane beside the times: smaller type for the narrower space. */
  readonly compact = input(false);
  /** For a 'Weather' item among the ads. */
  readonly weather = input<InfoscreenWeather | null>(null);
  /** For a 'News' item among the ads: the headlines and the station. */
  readonly news = input<InfoscreenNewsItem[]>([]);
  readonly newsSource = input<string | null>(null);

  readonly newsOnCard = computed(() => this.news().slice(0, NEWS_ON_CARD));
  readonly mixedNews = computed(() => new Set(this.news().map((item) => item.source)).size > 1);

  readonly index = signal(0);
  readonly fading = signal(false);

  readonly current = computed(() => {
    const ads = this.ads();
    return ads.length ? ads[this.index() % ads.length] : null;
  });

  private timer?: ReturnType<typeof setInterval>;
  private fadeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    // A new set of ads, or a new speed, starts the round again.
    effect(() => {
      const count = this.ads().length;
      const seconds = adPageSeconds(this.speed());
      untracked(() => this.restart(count, seconds));
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    clearTimeout(this.fadeTimer);
  }

  private restart(count: number, seconds: number): void {
    clearInterval(this.timer);
    clearTimeout(this.fadeTimer);
    this.fading.set(false);
    if (this.index() >= count) this.index.set(0);
    if (count < 2) return;
    this.timer = setInterval(() => {
      this.fading.set(true);
      this.fadeTimer = setTimeout(() => {
        this.index.update((i) => (i + 1) % count);
        this.fading.set(false);
      }, FADE_MS);
    }, seconds * 1000);
  }
}
